import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { AgentPageLayout } from "@/components/agents/AgentPageLayout";
import { FullbaseRunnerPanel } from "@/components/agents/FullbaseRunnerPanel";
import { WricefCatalogTab } from "@/components/agents/WricefCatalogTab";
import {
  DecidedScopeBreakdown,
  DecidedScopeMetrics,
} from "@/components/agents/DecidedScopeSummary";
import { OverviewView } from "@/components/agents/cleancore/OverviewView";
import { WorklistView } from "@/components/agents/cleancore/WorklistView";
import { useAgentExecutionContext } from "@/components/agents/AgentExecutionContext";
import { useWricefCatalogSummary, useWricefWorklist } from "@/hooks/useWricefCatalog";
import { useWricefDecisionSummary } from "@/hooks/useWricefDecisions";
import { useFullbaseScans, useFullbaseStatus } from "@/hooks/useFullbase";
import { fullbaseStageLabel } from "@/api/fullbase";
import { useResettingPage } from "@/hooks/useResettingPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAgentIdFromRoute } from "@/lib/agent-ids";
import { AlertTriangle, CheckCircle2, DollarSign, History, Layers, Lock, XCircle } from "lucide-react";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCurrentUser } from "@/hooks/useAuth";
import { useCcApproveTechnicalScope, useCcTechnicalScope } from "@/hooks/useCodeCompanion";

// ─────────────────────────────────────────────────────────────────────
// Effort tab
// ─────────────────────────────────────────────────────────────────────

interface ProfileSeniorityCost {
  days: number;
  rate_brl_day: number;
  subtotal: number;
}

interface CostBreakdown {
  total_effort_days: number;
  by_wricef_type: Record<string, number>;
  by_profile_seniority: Record<string, ProfileSeniorityCost>;
  overhead_multiplier: number;
  subtotal_brl: number;
  overhead_brl: number;
  total_brl: number;
  rate_card_version_pinned?: string;
  has_placeholders?: boolean;
  placeholder_profiles?: string[];
}

const WRICEF_LABELS: Record<string, string> = {
  W: i18n.t("Workflow"),
  R: i18n.t("Report"),
  I: i18n.t("Interface"),
  C: i18n.t("Conversion"),
  E: i18n.t("Enhancement"),
  F: "Form",
};

const PROFILE_LABELS: Record<string, string> = {
  abap_dev: i18n.t("ABAP Dev"),
  functional: i18n.t("Functional"),
  basis: i18n.t("Basis"),
  pm: "PM",
  architect: i18n.t("Architect"),
  qa: "QA",
};

const SENIORITY_LABELS: Record<string, string> = {
  junior: i18n.t("Junior"),
  pleno: i18n.t("Mid-level"),
  senior: i18n.t("Senior"),
  principal: i18n.t("Principal"),
};

function formatBrl(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatProfileSeniority(key: string): string {
  // "abap_dev_pleno" -> "ABAP Dev · Pleno"
  // Split on last "_" pra acomodar profiles com underscore
  const lastUnder = key.lastIndexOf("_");
  if (lastUnder === -1) return key;
  const profile = key.slice(0, lastUnder);
  const seniority = key.slice(lastUnder + 1);
  return `${PROFILE_LABELS[profile] ?? profile} · ${SENIORITY_LABELS[seniority] ?? seniority}`;
}

function WricefBar({
  type,
  days,
  maxDays,
}: {
  type: string;
  days: number;
  maxDays: number;
}) {
  const pct = maxDays > 0 ? Math.round((days / maxDays) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-24 shrink-0 font-mono">
        <Badge variant="outline" className="text-[10px] font-semibold">
          {type}
        </Badge>{" "}
        <span className="text-muted-foreground">
          {WRICEF_LABELS[type] ?? "?"}
        </span>
      </div>
      <div className="flex-grow h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-16 text-right tabular-nums">{days.toFixed(1)} d</div>
    </div>
  );
}

// Exportado para o teste isolado (#1503) renderizar sem o resto da página.
export function EffortTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { outputs, isComplete } = useAgentExecutionContext();
  const cost = (outputs?.cost_breakdown as CostBreakdown | undefined) ?? null;
  // Ao vivo, independente da execução congelada abaixo (#1503): o arquiteto
  // pode decidir escopo sem nunca ter rodado o agente de novo.
  const decisionSummaryQuery = useWricefDecisionSummary(projectId);
  const hasDecisionData = Boolean(decisionSummaryQuery.data?.universe);

  if (!cost) {
    return (
      <div className="space-y-4">
        {hasDecisionData && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DecidedScopeMetrics summary={decisionSummaryQuery.data} />
            </div>
            <DecidedScopeBreakdown summary={decisionSummaryQuery.data} />
          </>
        )}
        <Card className="shadow-none border">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <DollarSign className="mx-auto mb-3 h-6 w-6 opacity-60" />
            {isComplete
              ? t("Run finished but produced no cost estimate. Check whether the ref_rate_card / ref_effort_baseline catalog was populated.")
              : t("Run the agent to generate the effort and cost estimate. Results show up here.")}
          </CardContent>
        </Card>
      </div>
    );
  }

  const wricefEntries = Object.entries(cost.by_wricef_type).sort(
    ([, a], [, b]) => b - a,
  );
  const maxWricefDays = wricefEntries.reduce(
    (acc, [, v]) => Math.max(acc, v),
    0,
  );

  const profileEntries = Object.entries(cost.by_profile_seniority)
    .filter(([, v]) => v.days > 0)
    .sort(([, a], [, b]) => b.subtotal - a.subtotal);

  return (
    <div className="space-y-4">
      {cost.has_placeholders && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <strong>{t("Warning:")}</strong> rate card has values flagged as{" "}
            <code>is_placeholder=TRUE</code>. Functional/commercial must ratify
            before presenting a proposal to the client.
            {cost.placeholder_profiles && cost.placeholder_profiles.length > 0 && (
              <span className="block text-muted-foreground mt-1">
                Affected profiles: {cost.placeholder_profiles.join(", ")}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Escopo decidido completo (métricas + detalhamento) isolado em seu próprio bloco antes do congelado. */}
      {hasDecisionData && (
        <>
          <div className="space-y-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Decided scope")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DecidedScopeMetrics summary={decisionSummaryQuery.data} />
            </div>
          </div>

          <DecidedScopeBreakdown summary={decisionSummaryQuery.data} />

          <Separator />
        </>
      )}

      {/* Congelado da última execução do agente — o R$ por perfil continua
          sendo este, sem recalcular pelas horas decididas (#1503: isso é a
          #1343 D6, fora desta issue). */}
      <div className="space-y-2">
        <div className="text-[11px] text-muted-foreground">
          {t("Automatic estimate from the analysis")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-primary/30">
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("Total effort")}
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {cost.total_effort_days.toFixed(1)}
              </div>
              <div className="text-xs text-muted-foreground">person-days</div>
            </CardContent>
          </Card>
          <Card className="border-emerald-500/30">
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("Total cost")}
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {formatBrl(cost.total_brl)}
              </div>
              <div className="text-xs text-muted-foreground">
                Subtotal {formatBrl(cost.subtotal_brl)} + overhead{" "}
                {formatBrl(cost.overhead_brl)} (
                {((cost.overhead_multiplier - 1) * 100).toFixed(0)}%)
              </div>
            </CardContent>
          </Card>
          <Card className="border-sky-500/30">
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("Pinned rate card")}
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums font-mono">
                v{cost.rate_card_version_pinned ?? "?"}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("Atomic promotion via ref_data_active_versions")}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Distribuição WRICEF: mesma estimativa automática acima, por categoria (título diferencia do escopo decidido */}
      {wricefEntries.length > 0 && (
        <Card className="shadow-none border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t("Automatic estimate by WRICEF category")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {wricefEntries.map(([type, days]) => (
              <WricefBar
                key={type}
                type={type}
                days={days}
                maxDays={maxWricefDays}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Profile × seniority breakdown */}
      {profileEntries.length > 0 && (
        <Card className="shadow-none border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t("Breakdown by profile × seniority")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">{t("Profile")}</TableHead>
                  <TableHead className="text-xs text-right">{t("Days")}</TableHead>
                  <TableHead className="text-xs text-right">{t("Rate / day")}</TableHead>
                  <TableHead className="text-xs text-right">{t("Subtotal")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profileEntries.map(([key, val]) => (
                  <TableRow key={key}>
                    <TableCell className="text-xs">
                      {formatProfileSeniority(key)}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">
                      {val.days.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">
                      {val.rate_brl_day > 0
                        ? formatBrl(val.rate_brl_day)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">
                      {formatBrl(val.subtotal)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell className="text-xs">{t("Subtotal")}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">
                    {cost.total_effort_days.toFixed(2)}
                  </TableCell>
                  <TableCell />
                  <TableCell className="text-xs text-right tabular-nums">
                    {formatBrl(cost.subtotal_brl)}
                  </TableCell>
                </TableRow>
                <TableRow className="font-semibold">
                  <TableCell className="text-xs">
                    Overhead ({((cost.overhead_multiplier - 1) * 100).toFixed(0)}%)
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-xs text-right tabular-nums">
                    {formatBrl(cost.overhead_brl)}
                  </TableCell>
                </TableRow>
                <TableRow className="font-bold bg-muted/30">
                  <TableCell className="text-xs">{t("Total")}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-xs text-right tabular-nums">
                    {formatBrl(cost.total_brl)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Clean Core cockpit + worklist — read-only, lê do CATÁLOGO WRICEF
// ─────────────────────────────────────────────────────────────────────

// Espelha o padrão da Aderência (Functional): Visão Geral (cockpit) e
// Decisões & Remediação (worklist) derivam do MESMO catálogo que a aba WRICEF,
// não da saída `decisions` de um run. Por isso a varredura full-base (que popula
// o catálogo) também alimenta estas abas. As duas leem o banco agregado ou
// paginado (#922), e nunca um recorte de linhas.

function CleanCoreLoading() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-7 w-full" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function CleanCoreEmpty({
  view,
  isSnapshot = false,
}: {
  view: "overview" | "remediation";
  isSnapshot?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card className="shadow-none border">
      <CardContent className="py-12 text-center text-sm text-muted-foreground">
        <Layers className="mx-auto mb-3 h-6 w-6 opacity-60" />
        {isSnapshot ? (
          <>
            {t('This scan has no assigned objects — it likely predates snapshot tracking. Run a new scan or click "View consolidated" for the whole base.')}
          </>
        ) : (
          <>
            {view === "remediation"
              ? t("Empty catalog — run the base scan (panel above) to populate the clean core posture and the remediation backlog.")
              : t("Empty catalog — run the base scan (panel above) to populate the clean core posture.")}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CleanCoreError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Card className="shadow-none border">
      <CardContent className="py-10 text-center text-sm text-muted-foreground space-y-3">
        <XCircle className="mx-auto h-6 w-6 text-red-500 opacity-70" />
        <p>{t("Could not load the WRICEF catalog.")}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t("Try again")}
        </Button>
      </CardContent>
    </Card>
  );
}

function CleanCoreOverviewTab({
  projectId,
  scanJobId,
}: {
  projectId: string;
  scanJobId?: string | null;
}) {
  // O resumo do catálogo inteiro (#922). Antes eram as 1.000 primeiras linhas em
  // ordem alfabética, agregadas no navegador: num catálogo de 7.288 a aba dizia
  // "27 of 1.000" cloud-ready, com 620 no catálogo.
  const { data, isLoading, error, refetch } = useWricefCatalogSummary(projectId, scanJobId);
  // #1111: o retrato de um scan escolhido no histórico não muda; o acumulado
  // muda a cada lote enquanto a varredura roda.
  const { data: scanStatus } = useFullbaseStatus(projectId);
  const scanInProgress = !scanJobId && Boolean(scanStatus?.running);
  if (isLoading) return <CleanCoreLoading />;
  if (error || !data) return <CleanCoreError onRetry={() => refetch()} />;
  if ((data.total_rows ?? 0) === 0)
    return <CleanCoreEmpty view="overview" isSnapshot={Boolean(scanJobId)} />;
  return <OverviewView summary={data} scanInProgress={scanInProgress} />;
}

/** Cards por página na worklist. Um card tem várias linhas, a tabela tem uma. */
const WORKLIST_PAGE_SIZE = 25;

function CleanCoreRemediationTab({
  projectId,
  scanJobId,
}: {
  projectId: string;
  scanJobId?: string | null;
}) {
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set());
  const streams = useMemo(() => [...selectedStreams].sort(), [selectedStreams]);
  const [page, setPage] = useResettingPage(
    JSON.stringify({ streams, scanJobId: scanJobId ?? null }),
  );

  // Paginada e filtrada no banco (#922). Antes a aba lia as 1.000 primeiras
  // linhas do catálogo, em ordem alfabética, e montava a worklist sobre elas.
  const { data, isLoading, error, refetch } = useWricefWorklist(projectId, {
    ...(streams.length ? { stream: streams } : {}),
    ...(scanJobId ? { scan_job_id: scanJobId } : {}),
    limit: WORKLIST_PAGE_SIZE,
    offset: (page - 1) * WORKLIST_PAGE_SIZE,
  });
  // Separa "catálogo vazio" de "catálogo sem nada a decidir": as duas chegam
  // como worklist vazia, e só a primeira manda rodar a varredura.
  const summary = useWricefCatalogSummary(projectId, scanJobId);

  const toggleStream = useCallback((stream: string) => {
    setSelectedStreams((prev) => {
      const next = new Set(prev);
      if (next.has(stream)) next.delete(stream);
      else next.add(stream);
      return next;
    });
  }, []);

  if (isLoading) return <CleanCoreLoading />;
  if (error || !data) return <CleanCoreError onRetry={() => refetch()} />;
  if (summary.data && (summary.data.total_rows ?? summary.data.total) === 0)
    return <CleanCoreEmpty view="remediation" isSnapshot={Boolean(scanJobId)} />;
  return (
    <WorklistView
      data={data}
      selectedStreams={selectedStreams}
      onToggleStream={toggleStream}
      onClearStreams={() => setSelectedStreams(new Set())}
      page={page}
      pageSize={WORKLIST_PAGE_SIZE}
      onPageChange={setPage}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// Banner de snapshot — quando uma varredura está selecionada, comunica que
// as abas mostram só aquele recorte + botão pra voltar ao consolidado.
// ─────────────────────────────────────────────────────────────────────

function ScanSnapshotBanner({
  projectId,
  scanJobId,
  onClear,
}: {
  projectId: string;
  scanJobId: string;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const { data: scans = [] } = useFullbaseScans(projectId);
  const idx = scans.findIndex((s) => s.job_id === scanJobId);
  // Histórico vem mais recente primeiro; o nº da varredura é o ordinal de baixo.
  const runNumber = idx >= 0 ? scans.length - idx : null;

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 flex items-center justify-between gap-3">
      <span className="text-xs text-foreground flex items-center gap-2">
        <History className="h-3.5 w-3.5 text-primary" />
        {runNumber != null
          ? t("Viewing scan #{{n}} (snapshot): the tabs show only the objects cataloged in it.", {
              n: runNumber,
            })
          : t("Viewing a scan (snapshot): the tabs show only the objects cataloged in it.")}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1 shrink-0"
        onClick={onClear}
      >
        {t("View consolidated")}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────

// Conteúdo das abas — vive DENTRO do AgentExecutionProvider (o AgentPageLayout
// envolve os children), então pode ler o selectedScanId do contexto e propagá-lo
// pras abas + banner.
// Gate A: aprovação do escopo técnico (orçamento/horas). Vive aqui no Architect,
// logo abaixo do base scan. Aprovar auto-semeia o board do CC (libera o dev).
// Exportado para o teste isolado (#1503) renderizar sem o resto da página.
export function TechnicalScopeGate({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const isOwner = useCurrentUser()?.role === "admin";
  const { data } = useCcTechnicalScope(projectId);
  const approve = useCcApproveTechnicalScope(projectId);
  if (!data) return null;
  const approved = data.approved;

  return (
    <Card className={approved ? "border-emerald-500/40" : "border-amber-500/50"}>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex items-start gap-3">
          {approved ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
          ) : (
            <Lock className="mt-0.5 h-5 w-5 text-amber-600" />
          )}
          <div>
            <div className="text-sm font-semibold">
              {t("Technical scope · Gate A")}
            </div>
            <div className="text-sm text-muted-foreground">
              {/* #1503: o número é a decisão do arquiteto (ou o fallback
                  corrigido, sem decisão nenhuma) — não mais o catálogo bruto. */}
              {t("{{n}} objects in conversion scope · ~{{h}}h", {
                n: data.in_scope_count,
                h: data.total_effort_h,
              })}
              {approved
                ? ` · ${t("approved — development released to Code Companion")}`
                : ` · ${t("development starts only after approval")}`}
            </div>
            {data.pending_count > 0 && (
              <div className="mt-0.5 text-xs text-amber-700">
                {t("{{n}} objects pending client validation · ~{{h}}h", {
                  n: data.pending_count,
                  h: data.pending_effort_h,
                })}
              </div>
            )}
            {data.undecided_count > 0 && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t("{{n}} objects without a decision yet", { n: data.undecided_count })}
              </div>
            )}
            {approved && data.approved_by_name && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {t("Approved by {{who}}", { who: data.approved_by_name })}
                {data.approved_at ? ` · ${new Date(data.approved_at).toLocaleString()}` : ""}
              </div>
            )}
          </div>
        </div>
        {isOwner ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={approve.isPending} variant={approved ? "outline" : "default"}>
                {approve.isPending
                  ? t("Approving…")
                  : approved
                    ? t("Re-approve (re-seed)")
                    : t("Approve technical scope")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("Approve technical scope?")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "Releases {{n}} objects (~{{h}}h) to development in Code Companion and seeds the board. This is recorded as approved by you, with date and time.",
                    { n: data.in_scope_count, h: data.total_effort_h },
                  )}
                  {data.pending_count > 0 &&
                    ` ${t(
                      "{{n}} objects pending client validation stay out of this total and can be approved later.",
                      { n: data.pending_count },
                    )}`}
                  {data.undecided_count > 0 &&
                    ` ${t(
                      "{{n}} objects without a decision yet stay out of this total too.",
                      { n: data.undecided_count },
                    )}`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    approve.mutate(undefined, {
                      onSuccess: (r) =>
                        toast.success(
                          t("Technical scope approved · board seeded ({{n}} in scope)", {
                            n: r.in_scope_count,
                          }),
                        ),
                      onError: () => toast.error(t("Could not approve the technical scope")),
                    })
                  }
                >
                  {t("Confirm approval")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          !approved && (
            <span className="text-xs text-muted-foreground">
              {t("Awaiting owner approval")}
            </span>
          )
        )}
      </CardContent>
    </Card>
  );
}

function ArchitectBody({ projectId }: { projectId?: string }) {
  const { t } = useTranslation();
  const { selectedScanId, setSelectedScanId } = useAgentExecutionContext();
  const scanId = selectedScanId ?? null;

  return (
    <>
      {/* Controle: a varredura da base fica num card fixo acima dos resultados.
          Roda aqui em cima; o Catálogo (abaixo) atualiza ao vivo. */}
      {projectId && (
        <div className="mb-4">
          <FullbaseRunnerPanel projectId={projectId} />
        </div>
      )}

      {projectId && scanId && (
        <div className="mb-4">
          <ScanSnapshotBanner
            projectId={projectId}
            scanJobId={scanId}
            onClear={() => setSelectedScanId?.(null)}
          />
        </div>
      )}

      {projectId && (
        <div className="mb-4">
          <TechnicalScopeGate projectId={projectId} />
        </div>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">{t("Overview")}</TabsTrigger>
          <TabsTrigger value="remediation">{t("Decisions & Remediation")}</TabsTrigger>
          <TabsTrigger value="wricef">{t("WRICEF Catalog")}</TabsTrigger>
          <TabsTrigger value="effort">{t("Effort Estimation")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {projectId ? (
            <CleanCoreOverviewTab projectId={projectId} scanJobId={scanId} />
          ) : (
            <Card className="shadow-none border">
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {t("Select a project to view the Clean Core posture.")}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="remediation">
          {projectId ? (
            <CleanCoreRemediationTab projectId={projectId} scanJobId={scanId} />
          ) : (
            <Card className="shadow-none border">
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {t("Select a project to view the remediation backlog.")}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="wricef">
          {projectId ? (
            <WricefCatalogTab projectId={projectId} scanJobId={scanId} />
          ) : (
            <Card className="shadow-none border">
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {t("Select a project to view the WRICEF catalog.")}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="effort">
          {projectId ? (
            <EffortTab projectId={projectId} />
          ) : (
            <Card className="shadow-none border">
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {t("Select a project to view the effort estimate.")}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </>
  );
}

export default function Architect() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const agentId = getAgentIdFromRoute("architect");
  // A varredura é uma AÇÃO (controle), não um resultado — vive num card fixo no
  // topo, não numa aba entre os resultados. Como o painel está sempre visível
  // acima do Catálogo, a ponte recorte → base inteira é estrutural (sem CTA).

  // #853: o mesmo status que o painel já consulta. O React Query deduplica, e o
  // cabeçalho passa a saber que existe trabalho rodando fora da execução.
  const { data: scanStatus } = useFullbaseStatus(projectId);
  const scanBusy = scanStatus?.running ? t(fullbaseStageLabel(scanStatus)) : null;

  return (
    <AgentPageLayout
      agentId={agentId}
      agentName="Architect"
      description={t("WRICEF catalog, Clean Core decisions and migration estimate.")}
      // #853: a varredura é job próprio e dura horas. Sem isto o cabeçalho
      // dizia COMPLETED — verdade sobre a execução, mentira sobre a tela — ao
      // lado do card com a barra andando.
      backgroundActivity={scanBusy}
      agentNumber={2}
      totalAgents={8}
      agentBadge={t("Clean Core Catalog")}
      phaseLabel={t("Discover")}
    >
      <ArchitectBody projectId={projectId} />
    </AgentPageLayout>
  );
}
